import { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Modal,
  Pressable,
  ScrollView,
  Switch,
  Platform,
  KeyboardAvoidingView,
  StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { Calendar } from '@/components/ui/Calendar';
import { ModalHeader } from '@/components/ui/ModalHeader';
import { CityAutocomplete } from '@/components/ui/CityAutocomplete';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { createTrip, type CreateTripData, type TripDestination } from '@/services/trips';
import { MultiCityDestinationForm } from './MultiCityDestinationForm';
import type { TravelMode } from '@/services/navigation';
import { AutocompleteInput } from '@/components/ui/AutocompleteInput';
import { getTripNameSuggestions } from '@/utils/formSuggestions';
import { DESTINATION_OPTIONS, findDestinationLabel } from '@/data/destinationOptions';
import { useUserStore } from '@/state/userStore';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { isTripLimitEntitlementError } from '@/services/entitlements';
import { toIso2CountryCode } from '@/utils/countryIso2';

export type ManualTripFormPrefill = {
  tripName?: string;
  travelingFrom?: string;
  destination?: string;
  startDate?: Date;
  endDate?: Date;
  travelMode?: string;
};

type ManualTripFormProps = {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  initialValues?: ManualTripFormPrefill | null;
};

const TRAVEL_MODE_OPTIONS = [
  { value: 'plane', label: '✈️ Plane' },
  { value: 'car', label: '🚗 Car' },
  { value: 'train', label: '🚂 Train' },
  { value: 'bus', label: '🚌 Bus' },
  { value: 'boat', label: '⛵ Boat' },
  { value: 'mixed', label: '🔀 Mixed' },
];

const PREFERRED_TRAVEL_MODE_OPTIONS: Array<{ value: TravelMode; label: string; description: string }> = [
  { value: 'driving', label: '🚗 Driving', description: 'Best for longer distances and flexibility' },
  { value: 'walking', label: '🚶 Walking', description: 'Great for exploring cities on foot' },
  { value: 'transit', label: '🚇 Public Transit', description: 'Efficient for urban travel' },
];

// Destination options are shared via `src/data/destinationOptions.ts`

export function ManualTripForm({ visible, onClose, onSuccess, userId, initialValues }: ManualTripFormProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [tripName, setTripName] = useState('');
  const [travelingFrom, setTravelingFrom] = useState('');
  const [isMultiCity, setIsMultiCity] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState('');
  const [customDestination, setCustomDestination] = useState('');
  const [destinations, setDestinations] = useState<TripDestination[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [travelMode, setTravelMode] = useState('');
  const [preferredTravelMode, setPreferredTravelMode] = useState<TravelMode>('driving');
  const [emailSyncEnabled, setEmailSyncEnabled] = useState(false);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const profile = useUserStore((state) => state.profile);
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const hasInitializedOriginRef = useRef(false);

  const multiCityDestinationsProp = useMemo(() => {
    if (destinations.length > 0) return destinations;
    if (startDate && endDate) {
      return [{ city: '', startDate, endDate }];
    }
    return [];
  }, [destinations, startDate?.getTime(), endDate?.getTime()]);

  useEffect(() => {
    if (hasInitializedOriginRef.current) return;
    const candidate =
      (profile.homeCity && profile.homeCity.trim()) ||
      (profile.homeCountry && profile.homeCountry.trim()) ||
      '';
    if (candidate) {
      setTravelingFrom(candidate);
      hasInitializedOriginRef.current = true;
    }
  }, [profile.homeCity, profile.homeCountry]);

  useEffect(() => {
    if (!visible || !initialValues) return;

    setIsMultiCity(false);
    setDestinations([]);
    setTripName(initialValues.tripName?.trim() ?? '');
    setTravelingFrom(initialValues.travelingFrom?.trim() ?? '');

    const destination = initialValues.destination?.trim() ?? '';
    if (destination) {
      const matchingOption = DESTINATION_OPTIONS.find(
        (option) => option.label.toLowerCase() === destination.toLowerCase(),
      );
      if (matchingOption) {
        setSelectedDestination(matchingOption.value);
        setCustomDestination('');
      } else {
        setSelectedDestination('other');
        setCustomDestination(destination);
      }
    } else {
      setSelectedDestination('');
      setCustomDestination('');
    }

    const nextStartDate = initialValues.startDate ? new Date(initialValues.startDate) : null;
    const nextEndDate = initialValues.endDate ? new Date(initialValues.endDate) : null;
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setTravelMode(initialValues.travelMode ?? '');
    setErrors({});
  }, [visible, initialValues]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!tripName.trim()) {
      newErrors.tripName = 'Trip name is required';
    }

    // Validate destination(s)
    if (isMultiCity) {
      if (destinations.length === 0) {
        newErrors.destinations = 'At least one destination is required';
      } else {
        destinations.forEach((dest, index) => {
          if (!dest.city.trim()) {
            newErrors[`destination_${index}`] = `City ${index + 1} is required`;
          }
          if (dest.startDate >= dest.endDate) {
            newErrors[`destination_dates_${index}`] = `City ${index + 1}: End date must be after start date`;
          }
        });
      }
      // For multi-city trips, derive trip dates from destinations
      if (destinations.length > 0) {
        const earliestStart = destinations.reduce((earliest, dest) => 
          !earliest || dest.startDate < earliest ? dest.startDate : earliest, 
          null as Date | null
        );
        const latestEnd = destinations.reduce((latest, dest) => 
          !latest || dest.endDate > latest ? dest.endDate : latest, 
          null as Date | null
        );
        if (earliestStart) setStartDate(earliestStart);
        if (latestEnd) setEndDate(latestEnd);
      }
    } else {
      // For CityAutocomplete, check if we have a value
      const destinationValue =
        selectedDestination === 'other'
          ? customDestination
          : (findDestinationLabel(selectedDestination) || '');
      if (!destinationValue || !destinationValue.trim()) {
        newErrors.destination = 'Destination is required';
      } else if (selectedDestination === 'other' && !customDestination.trim()) {
        newErrors.customDestination = 'Please enter a custom destination';
      } else {
        // Require a destination that resolves to a country ISO2 (e.g. "Tokyo, Japan").
        // This makes visa/advisory services reliable.
        const inferredCountry = destinationValue.split(',').map((p) => p.trim()).pop() || destinationValue;
        const iso2 = toIso2CountryCode(inferredCountry);
        if (!iso2) {
          newErrors.destination =
            'Please select a destination that includes the country (e.g., "Tokyo, Japan").';
        }
      }
      
      // For single-city trips, validate trip-level dates
      if (!startDate) {
        newErrors.startDate = 'Start date is required';
      }

      if (!endDate) {
        newErrors.endDate = 'End date is required';
      } else if (startDate && endDate < startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (!travelMode) {
      newErrors.travelMode = 'Travel mode is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const tripData: CreateTripData = {
        name: tripName.trim(),
        travelingFrom: travelingFrom.trim() || undefined,
        travelMode,
        preferredTravelMode,
        emailSyncEnabled,
        baseCurrency: profile.defaultCurrency || 'USD',
        startDate: startDate ?? new Date(),
        endDate: endDate ?? new Date(),
      };

      if (isMultiCity) {
        tripData.destinations = destinations;
        // For multi-city create, derive trip-level dates from destinations.
        const starts = destinations.map((d) => d.startDate).filter(Boolean);
        const ends = destinations.map((d) => d.endDate).filter(Boolean);
        if (starts.length) tripData.startDate = new Date(Math.min(...starts.map((d) => d.getTime())));
        if (ends.length) tripData.endDate = new Date(Math.max(...ends.map((d) => d.getTime())));
      } else {
        // Determine final destination value from CityAutocomplete
        const destinationValue =
          selectedDestination === 'other'
            ? customDestination
            : (findDestinationLabel(selectedDestination) || '');
        tripData.destination = destinationValue.trim();
        tripData.startDate = startDate!;
        tripData.endDate = endDate!;
      }

      await createTrip(userId, tripData);
      
      // Reset form
      setTripName('');
      setTravelingFrom('');
      setIsMultiCity(false);
      setSelectedDestination('');
      setCustomDestination('');
      setDestinations([]);
      setStartDate(null);
      setEndDate(null);
      setTravelMode('');
      setEmailSyncEnabled(false);
      setErrors({});
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating trip:', error);
      if (isTripLimitEntitlementError(error)) {
        openPremiumGate({
          feature: 'trip_limit',
          title: '1 active trip on Free',
          message: 'The free plan includes 1 active trip. Upgrade to Premium for unlimited trips, or archive your current trip to start a new one.',
          source: 'feature_gate',
        });
        setErrors({ submit: 'Free plan includes 1 active trip. Upgrade to Premium or archive your current trip.' });
        return;
      }
      setErrors({ submit: 'Failed to create trip. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTripName('');
    setTravelingFrom('');
    setSelectedDestination('');
    setCustomDestination('');
    setStartDate(null);
    setEndDate(null);
    setTravelMode('');
    setEmailSyncEnabled(false);
    setErrors({});
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    onClose();
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleDestinationChange = (value: string) => {
    setSelectedDestination(value);
    if (value !== 'other') {
      setCustomDestination('');
      setErrors({ ...errors, customDestination: '' });
    }
  };


  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={handleClose}
      presentationStyle={Platform.OS === 'ios' ? 'fullScreen' : 'pageSheet'}
      accessibilityViewIsModal={true}
      accessibilityLabel="Plan your trip"
    >
      <SelectFieldModalProvider>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={0}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: theme.colors.surface,
          }}
        >
          <ModalHeader title="Plan your trip" onClose={handleClose} />

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              padding: theme.spacing(2),
              gap: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            }}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          >
            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <View
                accessibilityRole="alert"
                accessibilityLiveRegion="assertive"
                style={{
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.error + '15',
                  borderRadius: theme.radii.md,
                  borderWidth: 1,
                  borderColor: theme.colors.error + '40',
                  gap: theme.spacing(0.5),
                }}
              >
                <ThemedText variant="bodyStrong" color={theme.colors.error}>
                  Please fix the following errors:
                </ThemedText>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldLabels: Record<string, string> = {
                    tripName: 'Trip name',
                    destination: 'Destination',
                    customDestination: 'Custom destination',
                    destinations: 'Destinations',
                    startDate: 'Start date',
                    endDate: 'End date',
                    travelMode: 'Travel mode',
                  };
                  return (
                    <ThemedText key={field} variant="body" color={theme.colors.error}>
                      • {fieldLabels[field] || field}: {message}
                    </ThemedText>
                  );
                })}
              </View>
            )}

            <Card variant="outlined" style={{ overflow: 'visible' }}>
              <AutocompleteInput
                label="Trip name"
                required
                placeholder="e.g., Summer Vacation"
                value={tripName}
                onChangeText={(text) => {
                  setTripName(text);
                  if (errors.tripName) {
                    setErrors(prev => ({ ...prev, tripName: '' }));
                  }
                }}
                errorText={errors.tripName}
                autoCapitalize="words"
                suggestions={getTripNameSuggestions}
                minQueryLength={1}
                showSuggestionsOnFocus={true}
              />
            </Card>

            <Card variant="outlined" style={{ overflow: 'visible' }}>
              <CityAutocomplete
                label="Travelling from (optional)"
                placeholder="e.g., New York, London, or your city"
                value={travelingFrom}
                onChangeText={setTravelingFrom}
                allowUseAsEntered
              />
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                Your departure city helps us calculate more accurate flight costs
              </Caption>
            </Card>

            <Card variant="outlined" style={{ overflow: 'visible' }}>
              <View style={{ gap: theme.spacing(1.5) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <ThemedText variant="bodyStrong">Destination</ThemedText>
                  <Pressable
                    onPress={() => {
                      setIsMultiCity(!isMultiCity);
                      if (!isMultiCity && destinations.length === 0 && startDate && endDate) {
                        // Initialize with single destination
                        setDestinations([{
                          city: '',
                          startDate: startDate,
                          endDate: endDate,
                        }]);
                      }
                    }}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                      padding: theme.spacing(0.5),
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons 
                      name={isMultiCity ? "checkbox" : "square-outline"} 
                      size={20} 
                      color={theme.colors.primary} 
                    />
                    <ThemedText variant="body" color={theme.colors.primary} style={{ fontSize: 14 }}>
                      Multi-city trip
                    </ThemedText>
                  </Pressable>
                </View>

                {isMultiCity ? (
                  <MultiCityDestinationForm
                    destinations={multiCityDestinationsProp}
                    tripStartDate={startDate || new Date(0)} // Use epoch date if not set (will be treated as invalid)
                    tripEndDate={endDate || new Date(0)} // Use epoch date if not set (will be treated as invalid)
                    onChange={setDestinations}
                    constrainToTripDates={false}
                  />
                ) : (
                  <>
                    <CityAutocomplete
                      label="City / Destination"
                      placeholder="Search or enter a destination"
                      value={
                        selectedDestination === 'other'
                          ? customDestination
                          : (findDestinationLabel(selectedDestination) || '')
                      }
                      onChangeText={(text) => {
                        // Check if it matches any predefined destination
                        const matchingOption = DESTINATION_OPTIONS.find(opt => 
                          opt.label.toLowerCase() === text.toLowerCase()
                        );
                        if (matchingOption) {
                          setSelectedDestination(matchingOption.value);
                          setCustomDestination('');
                        } else {
                          setSelectedDestination('other');
                          setCustomDestination(text);
                        }
                        if (errors.destination || errors.customDestination) {
                          setErrors(prev => ({ ...prev, destination: '', customDestination: '' }));
                        }
                      }}
                      onSelectCity={(suggestion) => {
                        // Check if the selected city matches any predefined destination
                        const matchingOption = DESTINATION_OPTIONS.find(opt => 
                          opt.label.toLowerCase().includes(suggestion.city.toLowerCase()) ||
                          suggestion.fullName.toLowerCase().includes(opt.label.toLowerCase())
                        );
                        if (matchingOption) {
                          setSelectedDestination(matchingOption.value);
                          setCustomDestination('');
                        } else {
                          setSelectedDestination('other');
                          setCustomDestination(suggestion.fullName);
                        }
                      }}
                      allowUseAsEntered
                    />
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                      Tip: include the country (e.g., “Tokyo, Japan”) so we can check visa requirements accurately.
                    </Caption>
                    {errors.destination && (
                      <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                        {errors.destination}
                      </Caption>
                    )}
                    {errors.customDestination && (
                      <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                        {errors.customDestination}
                      </Caption>
                    )}
                  </>
                )}
                {errors.destinations && (
                  <Caption color={theme.colors.error}>{errors.destinations}</Caption>
                )}
              </View>
            </Card>

            {/* Only show trip-level dates for single-city trips */}
            {!isMultiCity && (
              <>
                <Card variant="outlined">
                  <View style={{ gap: theme.spacing(0.75) }}>
                    <ThemedText variant="bodyStrong" color={errors.startDate ? theme.colors.error : theme.colors.textSecondary}>
                      Start date
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Start date, ${startDate ? formatDate(startDate) : 'not set'}. Tap to change date`}
                      accessibilityHint="Opens calendar picker to select trip start date. Required field."
                      accessibilityRequired={true}
                      onPress={() => {
                        setShowStartCalendar(true);
                        if (errors.startDate) {
                          setErrors(prev => ({ ...prev, startDate: '' }));
                        }
                      }}
                      style={({ pressed }) => [
                        {
                          borderWidth: 1,
                          borderColor: errors.startDate
                            ? theme.colors.error
                            : theme.colors.border,
                          borderRadius: theme.radii.md,
                          paddingHorizontal: theme.spacing(2),
                          paddingVertical: theme.spacing(1.5),
                          backgroundColor: theme.colors.surface,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: pressed ? 0.85 : 1,
                          minHeight: 44,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="body"
                        color={startDate ? theme.colors.text : theme.colors.textSecondary}
                      >
                        {startDate ? formatDate(startDate) : 'Select start date'}
                      </ThemedText>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color={theme.colors.textSecondary}
                      />
                    </Pressable>
                    {errors.startDate && (
                      <Caption 
                        color={theme.colors.error}
                        accessibilityLiveRegion="polite"
                        accessibilityRole="alert"
                      >
                        {errors.startDate}
                      </Caption>
                    )}
                  </View>
                </Card>

                <Card variant="outlined">
                  <View style={{ gap: theme.spacing(0.75) }}>
                    <ThemedText variant="bodyStrong" color={errors.endDate ? theme.colors.error : theme.colors.textSecondary}>
                      End date
                    </ThemedText>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`End date, ${endDate ? formatDate(endDate) : 'not set'}. Tap to change date`}
                      accessibilityHint="Opens calendar picker to select trip end date. Required field."
                      accessibilityRequired={true}
                      onPress={() => {
                        setShowEndCalendar(true);
                        if (errors.endDate) {
                          setErrors(prev => ({ ...prev, endDate: '' }));
                        }
                      }}
                      style={({ pressed }) => [
                        {
                          borderWidth: 1,
                          borderColor: errors.endDate
                            ? theme.colors.error
                            : theme.colors.border,
                          borderRadius: theme.radii.md,
                          paddingHorizontal: theme.spacing(2),
                          paddingVertical: theme.spacing(1.5),
                          backgroundColor: theme.colors.surface,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          opacity: pressed ? 0.85 : 1,
                          minHeight: 44,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="body"
                        color={endDate ? theme.colors.text : theme.colors.textSecondary}
                      >
                        {endDate ? formatDate(endDate) : 'Select end date'}
                      </ThemedText>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color={theme.colors.textSecondary}
                      />
                    </Pressable>
                    {errors.endDate && (
                      <Caption 
                        color={theme.colors.error}
                        accessibilityLiveRegion="polite"
                        accessibilityRole="alert"
                      >
                        {errors.endDate}
                      </Caption>
                    )}
                  </View>
                </Card>
              </>
            )}

            <Card variant="outlined">
              <SelectField
                label="Travel mode"
                placeholder="Select how you'll travel"
                value={travelMode}
                options={TRAVEL_MODE_OPTIONS}
                onValueChange={setTravelMode}
                errorText={errors.travelMode}
              />
            </Card>

            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1) }}>
                <ThemedText variant="bodyStrong">Local Travel Preference</ThemedText>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                  How you'll get around during your trip (for itinerary scheduling)
                </Caption>
                <View style={{ gap: theme.spacing(1) }}>
                  {PREFERRED_TRAVEL_MODE_OPTIONS.map((option) => {
                    const isSelected = preferredTravelMode === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => setPreferredTravelMode(option.value)}
                        style={({ pressed }) => [
                          {
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: theme.spacing(1.5),
                            borderRadius: theme.radii.md,
                            borderWidth: 1,
                            borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                            backgroundColor: isSelected ? theme.colors.primary + '10' : theme.colors.surface,
                            opacity: pressed ? 0.7 : 1,
                            gap: theme.spacing(1),
                          },
                        ]}
                      >
                        <View style={{ flex: 1 }}>
                          <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
                            {option.label}
                          </ThemedText>
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: theme.spacing(0.25) }}>
                            {option.description}
                          </Caption>
                        </View>
                        {isSelected && (
                          <Ionicons name="checkmark-circle" size={24} color={theme.colors.primary} />
                        )}
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            </Card>

            <Card variant="outlined">
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: theme.spacing(1),
                }}
              >
                <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                  <ThemedText variant="bodyStrong">Sync email reservations</ThemedText>
                  <Caption color={theme.colors.textSecondary}>
                    Automatically import bookings from your email
                  </Caption>
                </View>
                <Switch
                  value={emailSyncEnabled}
                  onValueChange={setEmailSyncEnabled}
                  trackColor={{ false: theme.colors.borderMuted, true: theme.colors.primary }}
                  thumbColor={theme.colors.surface}
                />
              </View>
            </Card>

            {errors.submit && (
              <Card variant="outlined">
                <Body color={theme.colors.error}>{errors.submit}</Body>
              </Card>
            )}
          </ScrollView>

          {!(showStartCalendar || showEndCalendar) && (
          <View
            accessibilityLabel="Trip form actions"
            style={{
              flexDirection: 'row',
              gap: theme.spacing(1.5),
              paddingHorizontal: theme.spacing(2),
              paddingTop: theme.spacing(1.5),
              paddingBottom: Math.max(insets.bottom, theme.spacing(2)),
              borderTopWidth: StyleSheet.hairlineWidth,
              borderTopColor: theme.colors.borderMuted,
              backgroundColor: theme.colors.surface,
            }}
          >
            <Button
              label="Cancel"
              variant="ghost"
              onPress={handleClose}
              disabled={loading}
              accessibilityLabel="Cancel and close form"
              accessibilityHint="Discards any changes and closes the form"
              style={{ flex: 1 }}
            />
            <Button
              label={loading ? 'Creating...' : 'Create trip'}
              onPress={handleSubmit}
              disabled={loading}
              accessibilityLabel={loading ? 'Creating trip, please wait' : 'Create trip'}
              accessibilityState={{ disabled: loading, busy: loading }}
              style={{ flex: 1 }}
            />
          </View>
          )}

          {/* Calendar modals — inside flex root so iOS in-modal overlay covers the sheet */}
          <Calendar
            visible={showStartCalendar}
            onClose={() => setShowStartCalendar(false)}
            onSelectDate={(date) => {
              setStartDate(date);
              if (endDate && date > endDate) {
                setEndDate(null);
              }
            }}
            selectedDate={startDate}
            minDate={today}
            title="Select Start Date"
          />

          <Calendar
            visible={showEndCalendar}
            onClose={() => setShowEndCalendar(false)}
            onSelectDate={setEndDate}
            selectedDate={endDate}
            minDate={startDate || today}
            title="Select End Date"
          />
        </View>
      </KeyboardAvoidingView>
      </SelectFieldModalProvider>
    </Modal>
  );
}

